import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { NodoCluster, RuoloCluster } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, Spinner, timeAgo } from './common'
import { DataTable } from './DataTable'

/**
 * La testa della scheda di un cluster (Windows Failover Cluster, Exchange DAG):
 * che cluster è, i **nodi** con il loro stato e il loro CI, i **ruoli** con il
 * nodo che li ospita adesso e i VIP. Prima la scheda diceva «nodi: 3» e un
 * elenco di VIP sparsi negli attributi: non si capiva quale nodo mancasse né
 * dove girasse l'istanza SQL. Dati da `GET /cis/{id}/cluster`.
 */
export function SchedaCluster({ ciId }: { ciId: string }) {
  const dati = useAsync(() => api.ciCluster(ciId), [ciId])
  const d = dati.data
  if (dati.loading && !d) return <Spinner />
  if (!d?.is_cluster) return null
  const nodi = d.nodes ?? []
  const ruoli = d.roles ?? []
  const giu = nodi.filter((n) => n.state && n.state.toLowerCase() !== 'up')

  return (
    <>
      <Card title={t('Cluster')}>
        {giu.length > 0 && (
          <div className="banner banner--warn">
            {t('Nodi non attivi: {nodi}. I ruoli girano sugli altri nodi, ma il cluster ha meno margine per un altro guasto.', {
              nodi: giu.map((n) => `${n.name} (${n.state})`).join(', '),
            })}
          </div>
        )}
        <dl className="kv">
          <dt>{t('Tipo')}</dt>
          <dd><strong>{d.type_label ?? '—'}</strong></dd>
          {d.fqdn && (<><dt>{t('Nome DNS')}</dt><dd className="mono">{d.fqdn}</dd></>)}
          {d.domain && (<><dt>{t('Dominio')}</dt><dd>{d.domain}</dd></>)}
          <dt>{t('Nodi')}</dt>
          <dd>
            {nodi.some((n) => n.state)
              ? t('{su} attivi su {tot}', { su: d.nodes_up ?? 0, tot: d.node_count ?? nodi.length })
              : String(d.node_count ?? nodi.length)}
          </dd>
          {d.quorum && (<><dt>{t('Quorum')}</dt><dd>{d.quorum}</dd></>)}
          {d.witness && (<><dt>{t('Testimone')}</dt><dd className="mono">{d.witness}</dd></>)}
          {d.functional_level && (<><dt>{t('Livello funzionale')}</dt><dd>{d.functional_level}</dd></>)}
        </dl>
      </Card>

      <Card title={t('Nodi ({n})', { n: nodi.length })}>
        {nodi.length === 0 ? (
          <EmptyState title={t('Nessun nodo noto')} hint={t('I nodi compaiono quando l’inventario di almeno un membro (agente o scansione WinRM) arriva al Control Plane.')} />
        ) : (
          <DataTable<NodoCluster>
            rows={nodi}
            rowKey={(n) => n.name}
            initialSort={{ key: 'name' }}
            columns={[
              {
                key: 'name', header: t('Nodo'), value: (n) => n.name,
                render: (n) => (n.ci ? <Link to={`/cis/${n.ci.id}`}>{n.ci.name}</Link> : <span>{n.name}</span>),
              },
              {
                key: 'state', header: t('Stato'), value: (n) => n.state ?? '',
                render: (n) =>
                  !n.state ? <span className="muted">—</span>
                    : n.state.toLowerCase() === 'up' ? <span className="chip chip--ok">{n.state}</span>
                      : <span className="badge badge--warn">{n.state}</span>,
              },
              { key: 'ip', header: t('Indirizzo'), mono: true, value: (n) => n.ci?.mgmt_ip ?? '' },
              { key: 'os', header: t('Sistema operativo'), value: (n) => [n.ci?.os, n.ci?.os_version].filter(Boolean).join(' ') },
              { key: 'roles', header: t('Ruoli ospitati'), value: (n) => n.roles },
              {
                key: 'last_seen', header: t('Ultimo inventario'), value: (n) => n.ci?.last_seen ?? '',
                render: (n) => (n.ci ? timeAgo(n.ci.last_seen) : <span className="muted" title={t('Il nodo non è ancora in CMDB: installa l’agente o scansionalo via WinRM.')}>{t('non in CMDB')}</span>),
              },
            ]}
          />
        )}
      </Card>

      {ruoli.length > 0 && (
        <Card title={t('Ruoli ({n})', { n: ruoli.length })}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Cosa gira sul cluster e su quale nodo adesso: il nodo proprietario cambia a ogni failover.')}
          </p>
          <DataTable<RuoloCluster>
            rows={ruoli}
            rowKey={(r) => r.name}
            initialSort={{ key: 'name' }}
            columns={[
              { key: 'name', header: t('Ruolo'), value: (r) => r.name },
              { key: 'kind', header: t('Tipo'), value: (r) => r.kind_label ?? r.kind },
              {
                key: 'owner', header: t('Nodo attivo'), value: (r) => r.owner ?? '',
                render: (r) => (r.owner_ci_id ? <Link to={`/cis/${r.owner_ci_id}`}>{r.owner}</Link> : r.owner ?? '—'),
              },
              {
                key: 'state', header: t('Stato'), value: (r) => r.state ?? '',
                render: (r) =>
                  !r.state ? '—' : r.state.toLowerCase() === 'online'
                    ? <span className="chip chip--ok">{r.state}</span>
                    : <span className="badge badge--warn">{r.state}</span>,
              },
              { key: 'ips', header: t('VIP'), mono: true, value: (r) => r.ips.join(', ') },
            ]}
          />
        </Card>
      )}

      {(d.vips_without_role?.length ?? 0) > 0 && (
        <Card title={t('Altri indirizzi del cluster')}>
          <p className="mono" style={{ margin: 0 }}>{(d.vips_without_role ?? []).join(', ')}</p>
          <p className="field__hint">
            {t('VIP del cluster non associati a un ruolo nell’ultimo inventario (dagli inventari precedenti al dettaglio dei ruoli).')}
          </p>
        </Card>
      )}
    </>
  )
}
